import {
  ChangeEvent,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  DatumValue,
  InlineDataProps,
  InlineDataResult,
  InlineDataServiceParams,
  InlineDatum,
  InlineDatumBaseProps,
  InlineDatumExportValues,
  InlineDatumInput,
  InlineDatumProps,
} from './types';
import {ExportedValuesContext} from './ExportedValuesProvider';
import {InlineDataState} from './InlineDataState';
import {InlineDataContext} from './InlineDataProvider';
import {isReactComponentInlineDatum} from './helpers/datum-guard';

export const useInlineDataState = (inlineData: InlineDataProps) => {
  const {name, context, onDone} = inlineData;
  const exportedValues = useContext(ExportedValuesContext);
  const {service} = useContext(InlineDataContext);
  const [data, setData] = useState<Array<InlineDatum>>(inlineData.data);

  const state = useMemo(() => {
    return new InlineDataState(inlineData, service, setData, context);
  }, [inlineData, service, setData, context]);

  const doneRef = useRef<boolean>(false);
  const doneCallback = (result: InlineDataResult) => {
    if (doneRef.current) {
      return;
    }
    doneRef.current = true;

    if (onDone) {
      onDone(result);
    }
  };

  useEffect(() => {
    return () => state.clearPendingReload();
  }, [state]);

  useEffect(() => {
    data
      .filter((datum) => datum.datum_type === 'export')
      .forEach((datum) => {
        const exportDatum = datum as InlineDatumExportValues;
        const {export_values: exportValues} = exportDatum;
        if (exportValues) {
          const values = exportValues!;
          Object.keys(values).forEach((key) => {
            const value = values[key];
            exportedValues.setDatumValue(name, key, value);
          });
        }
      });
  }, [data, exportedValues, name]);

  const dataProps: Array<InlineDatumProps> = data.map((datum: InlineDatum) => {
    const {value} = datum;
    const datumWithValue = {...datum};

    if (datum.name && datum.import_from) {
      const exportedValue = exportedValues.getDatumValue(
        datum.import_from,
        datum.name,
      );
      if (exportedValue && isReactComponentInlineDatum(datum)) {
        datumWithValue.value = {
          ...((value ?? {}) as Record<string, unknown>),
          ...(exportedValue as Record<string, unknown>),
        };
      } else {
        datumWithValue.value = exportedValue ?? value;
      }
    }

    const baseProps: InlineDatumBaseProps = {
      datum: datumWithValue,
      fetch: (params) => state.fetch(params),
      action: (params) => state.action(params),
      onSubmit: (params?: InlineDataServiceParams) => state.onSubmit(params),
      onButtonClick: (params?: InlineDataServiceParams) =>
        state.onButtonClick(params),
      onDone: doneCallback,
      isDone: !!inlineData.isDone,
      outcome: inlineData.outcome,
      actionError: state.actionError,
      fetchError: state.fetchError,
    };

    let datumProps: InlineDatumProps;
    if (datum.datum_type === 'input') {
      datumProps = {
        ...baseProps,
        onChange: (e: ChangeEvent<HTMLInputElement> | DatumValue) =>
          state.onChange(datum as InlineDatumInput, e),
      };
    } else {
      datumProps = baseProps;
    }

    return datumProps;
  });

  return dataProps;
};
